import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { api, errorMessage } from "../../lib/api";
import { Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLOR = {
    draft: "bg-ink-100 text-ink",
    submitted: "bg-info/15 text-info",
    live: "bg-success/15 text-success",
    paused: "bg-warning/15 text-warning",
    rejected: "bg-error/15 text-error",
    completed: "bg-ink-100 text-ink-600",
    archived: "bg-ink-100 text-ink-600",
};

function bucketAlerts(items) {
    const out = [];
    for (const s of items) {
        if (!["live", "paused"].includes(s.status)) continue;
        for (const q of s.quotas || []) {
            const max = q.max_responses || 0;
            const cur = q.current_count || 0;
            if (max <= 0) continue;
            const pct = cur / max;
            if (pct >= 0.8) {
                out.push({
                    survey_id: s.id,
                    survey_title: s.title,
                    quota_id: q.id,
                    quota_name: q.name || q.id,
                    current: cur,
                    max,
                    pct: Math.round(pct * 100),
                    full: cur >= max,
                });
            }
        }
    }
    return out.sort((a, b) => b.pct - a.pct);
}

export default function AdvertiserDashboard() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [pendingExt, setPendingExt] = useState([]);
    const pending = user?.status !== "approved";

    const refreshPending = async () => {
        try {
            const r = await api.get("/extension-requests/mine");
            setPendingExt(r.data || []);
        } catch {
            /* no-op */
        }
    };

    useEffect(() => {
        api.get("/surveys/mine").then((r) => setItems(r.data || [])).catch(() => {});
        refreshPending();
    }, []);

    const live = items.filter((s) => s.status === "live").length;
    const completedResp = items.reduce((sum, s) => sum + (s.completed_count || 0), 0);
    const totalSpend = items.reduce((sum, s) => sum + (s.paid ? s.advertiser_cost_usd : 0), 0);
    const alerts = bucketAlerts(items);

    const pendingByKey = new Map();
    const STALE_MS = 24 * 60 * 60 * 1000;
    pendingExt.forEach((r) => {
        if (r.status !== "pending") return;
        const ageMs = Date.now() - new Date(r.created_at).getTime();
        if (ageMs >= STALE_MS) return; // stale → no longer blocks re-request
        pendingByKey.set(`${r.survey_id}:${r.quota_id || ""}`, r);
    });
    const pendingKeys = new Set(pendingByKey.keys());

    const requestExtend = async (a, e) => {
        e.preventDefault();
        e.stopPropagation();
        const ans = prompt(`Request more responses for bucket "${a.quota_name}" (currently ${a.current}/${a.max}). How many additional?`, "50");
        if (!ans) return;
        const additional = Number(ans);
        if (!additional || additional < 1) return;
        const note = prompt("Optional note for admin?") || "";
        try {
            await api.post("/extension-requests", {
                survey_id: a.survey_id,
                quota_id: a.quota_id,
                requested_additional: additional,
                note,
            });
            toast.success("Extension request sent — admin notified · we respond within 4h");
            await refreshPending();
        } catch (err) {
            toast.error(errorMessage(err));
        }
    };

    const selfExtend = async (a, e) => {
        e.preventDefault();
        e.stopPropagation();
        const ans = prompt(`Top up bucket "${a.quota_name}" instantly. How many additional? (Charged to your card via Stripe)`, "50");
        if (!ans) return;
        const additional = Number(ans);
        if (!additional || additional < 1) return;
        try {
            const { data } = await api.post(`/surveys/${a.survey_id}/self-extend`, {
                quota_extensions: [{ quota_id: a.quota_id, additional }],
            });
            toast.success(`Top-up applied (+${additional} to bucket)${data.cost_delta_usd > 0 ? ` · charged $${data.cost_delta_usd.toFixed(2)}` : ""}`);
            const fresh = await api.get("/surveys/mine");
            setItems(fresh.data || []);
        } catch (err) {
            toast.error(errorMessage(err));
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <p className="eyebrow mb-2 text-advertiser">ADVERTISER · {user?.advertiser_company?.name?.toUpperCase()}</p>
                        <h1 className="h2" data-testid="adv-welcome">Hello, {user?.name?.split(" ")[0]}.</h1>
                    </div>
                    <Link to="/advertiser/surveys/new" className="btn btn-advertiser" data-testid="adv-new-survey-btn">
                        <Plus size={18} /> New survey
                    </Link>
                </div>

                {pending && (
                    <div className="bg-warning/10 border border-warning/40 px-5 py-4 mb-8 text-sm" data-testid="adv-pending-banner">
                        <span className="font-semibold">Advertiser account pending admin approval.</span> You can draft surveys but not publish until approved.
                    </div>
                )}

                <div className="grid sm:grid-cols-3 gap-0 border border-ink-200 mb-12">
                    <Stat label="Live surveys" value={live} />
                    <Stat label="Total responses" value={completedResp} />
                    <Stat label="Total spend" value={`$${totalSpend.toFixed(2)}`} />
                </div>

                {alerts.length > 0 && (
                    <section className="mb-10" data-testid="adv-bucket-alerts">
                        <p className="eyebrow mb-3 flex items-center gap-2 text-warning">
                            <Warning size={14} weight="fill" /> BUCKETS RUNNING LOW
                        </p>
                        <div className="card-base">
                            {alerts.map((a) => (
                                <div
                                    key={`${a.survey_id}-${a.quota_id}`}
                                    className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-ink-200 last:border-b-0 items-center"
                                    data-testid={`bucket-alert-${a.survey_id}-${a.quota_id}`}
                                >
                                    <Link to={`/advertiser/surveys/${a.survey_id}`} className="col-span-12 md:col-span-4 truncate hover:underline">
                                        <div className="font-heading font-bold text-sm truncate">{a.survey_title}</div>
                                        <div className="text-xs text-ink-600 truncate">{a.quota_name}</div>
                                    </Link>
                                    <div className="col-span-7 md:col-span-4">
                                        <div className="h-2 bg-ink-100">
                                            <div className={`h-full ${a.full ? "bg-error" : "bg-warning"}`} style={{ width: `${Math.min(100, a.pct)}%` }} />
                                        </div>
                                    </div>
                                    <div className="col-span-5 md:col-span-2 text-right font-mono text-xs">
                                        {a.current}/{a.max} · {a.pct}%
                                        {a.full && <span className="ml-2 text-error">FULL</span>}
                                    </div>
                                    <div className="col-span-12 md:col-span-2 text-right flex flex-col sm:flex-row gap-2 justify-end">
                                        <button
                                            onClick={(e) => selfExtend(a, e)}
                                            className="btn btn-advertiser text-xs py-1 px-3"
                                            data-testid={`top-up-${a.survey_id}-${a.quota_id}`}
                                        >
                                            Top up now
                                        </button>
                                        {pendingKeys.has(`${a.survey_id}:${a.quota_id}`) ? (
                                            <PendingPill
                                                req={pendingByKey.get(`${a.survey_id}:${a.quota_id}`)}
                                                surveyId={a.survey_id}
                                                quotaId={a.quota_id}
                                            />
                                        ) : (
                                            <button
                                                onClick={(e) => requestExtend(a, e)}
                                                className="btn btn-outline text-xs py-1 px-3"
                                                data-testid={`request-extend-${a.survey_id}-${a.quota_id}`}
                                            >
                                                Request review
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-ink-600 mt-2">
                            Buckets at 80%+ — once full, matching respondents stop seeing the survey. Top up instantly via Stripe, or request admin review (~4h SLA).
                        </p>
                    </section>
                )}

                <p className="eyebrow mb-3">YOUR SURVEYS</p>
                <div className="card-base">
                    {items.length === 0 && (
                        <div className="p-10 text-center text-sm text-ink-600">
                            No surveys yet.{" "}
                            <Link to="/advertiser/surveys/new" className="underline">Create your first one.</Link>
                        </div>
                    )}
                    {items.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-4 p-5 border-b border-ink-200 last:border-b-0" data-testid={`adv-survey-${s.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-bold truncate">{s.title}</div>
                                <div className="text-xs text-ink-600 mt-1">
                                    {s.category} · {s.questions.length} q · {s.completed_count}/{s.responses_needed} responses · ${s.advertiser_cost_usd}
                                </div>
                            </div>
                            <span className={`font-mono text-[10px] px-2 py-1 ${STATUS_COLOR[s.status] || ""}`}>
                                {s.status.toUpperCase()}
                            </span>
                            <div className="flex gap-2 shrink-0">
                                {(s.status === "draft" || s.status === "rejected") && (
                                    <Link to={`/advertiser/surveys/${s.id}/edit`} className="btn btn-outline text-xs py-2 px-3" data-testid={`adv-edit-${s.id}`}>Edit</Link>
                                )}
                                <Link to={`/advertiser/surveys/${s.id}`} className="btn btn-ink text-xs py-2 px-3" data-testid={`adv-view-${s.id}`}>View</Link>
                            </div>
                        </div>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="p-6 border-r border-ink-200 last:border-r-0">
            <div className="font-mono text-3xl">{value}</div>
            <div className="text-xs uppercase tracking-wider text-ink-600 mt-1">{label}</div>
        </div>
    );
}



function formatAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

function PendingPill({ req, surveyId, quotaId }) {
    if (!req) return null;
    const ageMs = Date.now() - new Date(req.created_at).getTime();
    const slaMs = 4 * 60 * 60 * 1000;
    const remaining = Math.max(0, slaMs - ageMs);
    const remH = Math.floor(remaining / 3600000);
    const remM = Math.floor((remaining % 3600000) / 60000);
    const etaText = remaining > 0 ? `est. response in ${remH}h ${remM}m` : "overdue — SLA passed";
    return (
        <span
            className="relative group font-mono text-[10px] px-2 py-2 bg-info/15 text-info border border-info/40 inline-flex items-center justify-center text-center leading-tight cursor-help"
            data-testid={`pending-review-${surveyId}-${quotaId}`}
        >
            PENDING ADMIN REVIEW · 4h SLA
            <span
                role="tooltip"
                className="absolute right-0 top-full mt-2 hidden group-hover:block z-30 bg-ink text-paper p-3 w-72 normal-case text-left text-xs font-body shadow-lg"
                data-testid={`pending-review-tooltip-${surveyId}-${quotaId}`}
            >
                <div className="font-heading font-bold text-sm mb-1">+{req.requested_additional} additional</div>
                <div className="opacity-70">submitted {formatAgo(req.created_at)} · {etaText}</div>
                {req.note && <div className="italic opacity-90 mt-2 border-t border-paper/20 pt-2">&ldquo;{req.note}&rdquo;</div>}
            </span>
        </span>
    );
}
